import { PageHeader } from '@ant-design/pro-layout'
import { useMutation, useSuspenseQuery } from '@apollo/client'
import { FetchMeDocument, type LangEnum, UpdateMyUserDocument } from '@salescore/client-api'
import { getLocalStorageFlags, type LocalStorageFlag, setLocalStorageFlags } from '@salescore/client-base'
import {
  checkSyncEnabled,
  getOrganizationIdFromPath,
  HandleQuery,
  SuspenseWithLoading,
  UploadImageButton,
  UserAvatar,
} from '@salescore/client-common'
import { SlackIntegrationForm, UserSourcesTable } from '@salescore/client-feature-setting'
import { organizationHasFeature } from '@salescore/frontend-common'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Card, Col, Collapse, Form, Input, message, Row, Select, Space, Switch } from 'antd'
import { type ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

interface FormType {
  name: string
  lang?: LangEnum
}

const Page = (): ReactElement => {
  const { oauthCode, provider, state } = Route.useSearch()
  const { t } = useTranslation()
  const [updateMyUserMutation, { loading }] = useMutation(UpdateMyUserDocument)
  const {
    data: { myUser },
    refetch,
  } = useSuspenseQuery(FetchMeDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
  })
  const [imageUrl, setImageUrl] = useState('')

  const onImageUploaded = (imageUrl: string): void => {
    setImageUrl(imageUrl)
  }

  const onFinish = (values: FormType): void => {
    void updateMyUserMutation({
      variables: {
        organizationId: getOrganizationIdFromPath(),
        myUser: {
          name: values.name,
          lang: values.lang,
          imageUrl: imageUrl === '' ? undefined : imageUrl,
        },
      },
      onCompleted: () => {
        void refetch()
        void message.info(t('アカウント情報を更新しました！'))
      },
      onError: () => {
        void message.error(t(`アカウント情報の更新に失敗しました`))
      },
    })
  }

  const { organization } = myUser
  const commentFeatureEnabled = organizationHasFeature(organization, 'enable_slack_comment')
  return (
    <HandleQuery>
      <div className="m-4">
        <Card className="mb-8">
          <PageHeader title={t('アカウント設定')}>
            <Form wrapperCol={{ span: 8 }} onFinish={onFinish} initialValues={{ ...myUser }}>
              <Form.Item
                name={'name'}
                label={t('ユーザー名')}
                rules={[{ required: true, message: t('ユーザー名を入力してください') }]}
              >
                <Input width={200} disabled={myUser.organization.setting.disableEditUsername}></Input>
              </Form.Item>

              <Form.Item name="imageUrl" label={t('プロフィール画像')}>
                <Space>
                  <UserAvatar
                    name={myUser.name}
                    imageUrl={imageUrl === '' ? (myUser.imageUrl ?? '') : imageUrl}
                    size="large"
                  />
                  <UploadImageButton onFinish={onImageUploaded} />
                </Space>
              </Form.Item>
              {organization.setting.i18nEnabled && (
                <Form.Item name="lang" label={t(`言語設定`)}>
                  <Select
                    style={{ width: 100 }}
                    options={[
                      {
                        label: t(`日本語`),
                        value: 'ja',
                      },
                      {
                        label: t(`英語`),
                        value: 'en',
                      },
                    ]}
                  />
                </Form.Item>
              )}
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {t('更新')}
                </Button>
              </Form.Item>
            </Form>
          </PageHeader>
        </Card>
        {commentFeatureEnabled && <SlackIntegrationForm isOrg={false} organizationId={organization.id} />}
        {checkSyncEnabled(myUser) && (
          <Card className="mb-8">
            <PageHeader title={t(`連携設定(個人)`)}>
              <SuspenseWithLoading type="table">
                <UserSourcesTable oauthCode={oauthCode} provider={provider} state={state} />
              </SuspenseWithLoading>
            </PageHeader>
          </Card>
        )}
        <Card className="mb-8">
          <LocalStorageFlagsForm />
        </Card>
      </div>
    </HandleQuery>
  )
}

function LocalStorageFlagsForm(): ReactElement {
  const value = getLocalStorageFlags()
  const { t } = useTranslation()
  return (
    <Collapse defaultActiveKey={[]} className="mb-4">
      <Collapse.Panel header={t('高度な設定')} key="detail">
        <Form
          layout="vertical"
          initialValues={value}
          onFinish={(value) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            setLocalStorageFlags(value as LocalStorageFlag)
            void message.success(t(`保存しました`))
          }}
        >
          <Row>
            <Col span={12}>
              <Form.Item name="shouldPreventPopstate" label={t('ブラウザバックを無効化')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Button type="primary" htmlType="submit">
              {t('保存')}
            </Button>
          </Row>
        </Form>
      </Collapse.Panel>
    </Collapse>
  )
}

export const Route = createFileRoute('/o/$organizationId/v2/settings/me')({
  component: Page,
  validateSearch: (search) =>
    z
      .object({
        oauthCode: z.string().optional(),
        state: z.string().optional(),
        provider: z.string().optional(),
      })
      .parse(search),
})
